import { Fragment, useEffect, useRef, useState } from "react";

// Hooks
import iconSetFullScreen from "../../../assets/images/fullscreen.svg";
import iconSeekbarExpand from "../../../assets/images/seekbar-expand.svg";
import iconLiveSeekbar from "../../../assets/images/seekbar-live.svg";
import transitionIcon from "../../../assets/images/transition.svg";
import iconVolume from "../../../assets/images/volume.svg";
import useDidMountEffect from "../../../hooks/useDidMountEffect";
import * as FnSeekbar from "../__fn.seekbar";
import PlayPauseButton from "../button-play-pause";
import VolumeController from "../volume-controller";

const Seekbar = ({ openFullscreen }) => {
  const [showVolumeController, setShowVolumeController] = useState(false);
  const [volume, setVolume] = useState(50);
  const [seekbarData] = useState({
    seekbar_screens_list: [],
    skip_ranges: [],
  });
  const [screenWidths, setScreenWidths] = useState([]);
  const [seekbarState, setSeekbarState] = useState({
    value: 0,
    time: "00:00:00",
    offset: 0,
    max: 400, // ToDo: this should be read from server
    fakeMax: 400,
    left: 0,
    width: 0,
  });
  const [cuts] = useState([]);

  const seekbarRef = useRef();

  const between = (x, min, max) => x >= min && x <= max;

  // const sum = (arr) => arr.reduce((a, b) => a + b, 0);

  // const updateTimeRangeCut = (screens, cutRanges) => {
  //   const cutDurations = [];
  //   let prevScreenEnd = 0;
  //
  //   for (let i = 0; i < screens.length; i++) {
  //     const screen = screens[i];
  //     const [s, e] = screen.timeRange;
  //     let cd = 0;
  //     for (let j = 0; j < cutRanges.length; j++) {
  //       const [cf, ct] = cutRanges[j];
  //
  //       if (between(cf, s, e)) cd += ct - cf;
  //     }
  //
  //     cutDurations.push(cd);
  //
  //     const start = prevScreenEnd + (i === 0 ? 0 : 1);
  //     const end = e - sum(cutDurations);
  //
  //     screen.timeRangeCut = [start, end];
  //
  //     prevScreenEnd = end;
  //   }
  //
  //   return [screens, cutRanges, sum(cutDurations)];
  // };

  const calculateScreenWidths = () => {
    const widths = [];
    seekbarData.seekbar_screens_list.forEach((x) => {
      const [s, e] = x.timeRangeCut;
      const width = FnSeekbar.timeToPercentage(e - s, seekbarState.fakeMax, seekbarState.width);

      widths.push(width);
    });

    setScreenWidths(widths);
  };

  const getPercentageByTime = (time) => FnSeekbar.timeToPercentage(time, seekbarState.fakeMax, seekbarState.width);

  const getFakeValue = (realTime) => {
    let clone = [...cuts];

    clone = clone.sort(([a, b], [c, d]) => a - c || d - b);

    const i = clone.findIndex((x) => between(realTime, x[0], x[1]));

    if (i === 0) return realTime;

    let cd = 0;
    for (let j = i - 1; j >= 0; j--) {
      const [s, e] = clone[j];
      cd += e - s;
    }

    return realTime - cd;
  };

  const handleSeekbarChange = (e) => setSeekbarState((p) => ({ ...p, value: e.target.value }));

  useEffect(() => {
    const { left, width } = seekbarRef.current.getBoundingClientRect();
    setSeekbarState((prevState) => ({ ...prevState, width, left }));
  }, []);

  useDidMountEffect(() => {
    calculateScreenWidths();
  }, [seekbarState.fakeMax]);

  return (
    <div className="seekbar-wrapper">
      <div className="seekbar-container">
        {/* seekbar-btn */}
        <PlayPauseButton />

        <span className="flex expand-seekbar">
          <img src={iconSeekbarExpand} alt="seekbar-collapse" className="btn-16 btn-expand-seekbar m-lr-5" />
        </span>
        {/* seekbar */}
        <div className="seekbar-slider" ref={seekbarRef}>
          {/* screen sections */}
          {seekbarData.seekbar_screens_list.map((x, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={i}>
              <div
                className="screen-item"
                style={{
                  width: screenWidths[i],
                  backgroundColor: x.color,
                }}>
                {/* eslint-disable-next-line no-shadow */}
                {x.comments.map((time, i) => (
                  <span
                    /* eslint-disable-next-line react/no-array-index-key */
                    key={i}
                    style={{
                      width: FnSeekbar.timeToPercentage(time, seekbarState.fakeMax, seekbarState.width),
                    }}
                  />
                ))}
              </div>
              {i < seekbarData.seekbar_screens_list.length - 1 && (
                <div className="transition-item">
                  <img src={transitionIcon} alt="transition" />
                </div>
              )}
            </Fragment>
          ))}

          {cuts.map((c, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i}>
              <div
                data-time={c[0]}
                className="fn__cutter fn__marker fn__cutter-marker--expanded fn__marker--start"
                style={{
                  left: getPercentageByTime(getFakeValue(c[0])),
                }}
              />
              <div
                data-time={c[1]}
                className="fn__cutter fn__marker fn__cutter-marker--expanded fn__marker--end"
                style={{
                  left: getPercentageByTime(getFakeValue(c[0])),
                }}
              />
            </div>
          ))}

          <input
            type="range"
            min="0"
            max={seekbarState.fakeMax}
            id="slider-range"
            className="seekbar-input"
            value={seekbarState.value}
            onChange={handleSeekbarChange}
          />
          <span id="slider-value-in-player" style={{ left: seekbarState.offset }}>
            {seekbarState.time}
          </span>
        </div>

        {/* seekbar-btn */}
        <span className="flex relative" id="show-volume-controller">
          <img
            src={iconVolume}
            alt="volume"
            className={`btn-22 btn-volume relative z-index-10 ${showVolumeController ? "active" : null} `}
            onClick={() => setShowVolumeController(!showVolumeController)}
          />
          <VolumeController show={showVolumeController} onChange={(e) => setVolume(e.target.value)} value={volume} />
        </span>
        <span className="flex relative p-lr-5" id="go-to-fullscreen">
          <img src={iconSetFullScreen} alt="fullscreen" className="btn-22 btn-fullscreen-mode" onClick={openFullscreen} />
          <div className="tooltip-type2"> Full Screen </div>
        </span>
        <span className="flex">
          <img src={iconLiveSeekbar} alt="live" className="btn-36 btn-seekbar-live" />
        </span>
      </div>
    </div>
  );
};

export default Seekbar;
